import { supabase } from '../lib/supabase';

export interface JobSearchResult {
  id: string;
  name: string;
  description: string;
  salary: number;
  salary_type: 'hourly' | 'fixed' | 'negotiable';
  location: string;
  latitude?: number;
  longitude?: number;
  location_name?: string;
  job_type: string;
  experience_level?: string;
  company: string;
  created_at: string;
  distance?: number; // Distance in kilometers from search center
}

export interface RadiusSearchParams {
  centerLat: number;
  centerLng: number;
  radiusKm: number;
}

export class JobSearchService {
  /**
   * Search for jobs within a specified radius using Haversine formula
   * This function calculates the distance between the search center and each job's coordinates
   */
  static async searchJobsInRadius(params: RadiusSearchParams): Promise<JobSearchResult[]> {
    try {
      const { centerLat, centerLng, radiusKm } = params;
      
      console.log('🔍 Starting radius search with params:', {
        centerLat,
        centerLng,
        radiusKm,
        timestamp: new Date().toISOString()
      });
      
      // First, let's test if we can access the products table for jobs
      console.log('🔍 Testing database access...');
      const { data: testData, error: testError } = await supabase
        .from('products')
        .select('id, latitude, longitude, location_name')
        .eq('is_job', true)
        .limit(5);
        
      console.log('🔍 Test query result:', {
        testData,
        testError,
        count: testData?.length || 0
      });
      
      // First, get all jobs with coordinates from products table
      const { data: jobs, error } = await supabase
        .from('products')
        .select(`
          id,
          latitude,
          longitude,
          location_name
        `)
        .eq('is_job', true)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);

      console.log('🔍 Main query result:', {
        jobs,
        error,
        count: jobs?.length || 0,
        hasError: !!error
      });

      if (error) {
        console.error('❌ Error fetching jobs:', error);
        throw new Error(`Failed to fetch jobs: ${error.message}`);
      }

      if (!jobs || jobs.length === 0) {
        console.log('❌ No jobs found in database with coordinates');
        return [];
      }

      console.log('✅ Found jobs with coordinates:', jobs.map(j => ({
        id: j.id,
        location: j.location_name,
        lat: j.latitude,
        lng: j.longitude
      })));

      // Filter jobs within radius using Haversine formula
      const jobsInRadius: JobSearchResult[] = [];
      
      // Test distance calculation with known Neuss coordinates
      const neussLat = 51.19817780;
      const neussLng = 6.69164760;
      const testDistance = JobSearchService.calculateDistance(centerLat, centerLng, neussLat, neussLng);
      console.log('🧪 Distance test to Neuss:', {
        from: { lat: centerLat, lng: centerLng },
        to: { lat: neussLat, lng: neussLng },
        distance: Math.round(testDistance * 100) / 100,
        radiusKm,
        withinRadius: testDistance <= radiusKm
      });
      
      for (const job of jobs) {
        if (job.latitude && job.longitude) {
          console.log('🔍 Processing job:', {
            id: job.id,
            location: job.location_name,
            coordinates: { lat: job.latitude, lng: job.longitude },
            centerCoordinates: { lat: centerLat, lng: centerLng }
          });

          const distance = JobSearchService.calculateDistance(
            centerLat,
            centerLng,
            job.latitude,
            job.longitude
          );

          // Also calculate simple distance for comparison
          const simpleDistance = JobSearchService.calculateSimpleDistance(
            centerLat,
            centerLng,
            job.latitude,
            job.longitude
          );
          
          console.log('🔍 Distance comparison:', {
            jobId: job.id,
            location: job.location_name,
            haversineDistance: Math.round(distance * 100) / 100,
            simpleDistance: Math.round(simpleDistance * 100) / 100,
            radiusKm,
            withinRadius: distance <= radiusKm
          });
          
          if (distance <= radiusKm) {
            console.log(`✅ Found job within radius:`, {
              jobId: job.id,
              location: job.location_name,
              coordinates: { lat: job.latitude, lng: job.longitude },
              distance: Math.round(distance * 100) / 100,
              radiusKm,
              withinRadius: distance <= radiusKm
            });
            
            jobsInRadius.push({
              id: job.id, // Use id from company_jobs table
              name: `Company Job ${job.id.substring(0, 8)}`, // Generate a name since company_jobs table doesn't have job names
              description: `Company job from ${job.location_name || 'Unknown Location'}`,
              salary: 0, // Applications table doesn't have salary info
              salary_type: 'negotiable' as const,
              location: job.location_name || 'Unknown',
              latitude: job.latitude,
              longitude: job.longitude,
              location_name: job.location_name,
              job_type: 'full-time' as const,
              experience_level: 'mid-level' as const,
              company: 'Unknown Company',
              created_at: new Date().toISOString(),
              distance: Math.round(distance * 100) / 100 // Round to 2 decimal places
            });
          } else {
            console.log(`❌ Job outside radius:`, {
              jobId: job.id,
              location: job.location_name,
              coordinates: { lat: job.latitude, lng: job.longitude },
              distance: Math.round(distance * 100) / 100,
              radiusKm,
              withinRadius: distance <= radiusKm
            });
          }
        } else {
          console.log('❌ Job missing coordinates:', {
            id: job.id,
            location: job.location_name,
            hasLatitude: !!job.latitude,
            hasLongitude: !!job.longitude
          });
        }
      }

      // If no jobs found with exact coordinates, try a more lenient search
      if (jobsInRadius.length === 0 && jobs.length > 0) {
        console.log('🔍 No jobs found with exact coordinates, trying lenient search...');
        
        for (const job of jobs) {
          if (job.latitude && job.longitude) {
            // Check if coordinates are very close (within 0.01 degrees, roughly 1km)
            const latDiff = Math.abs(job.latitude - centerLat);
            const lngDiff = Math.abs(job.longitude - centerLng);
            
            console.log('🔍 Lenient search check:', {
              jobId: job.id,
              location: job.location_name,
              coordinates: { lat: job.latitude, lng: job.longitude },
              centerCoordinates: { lat: centerLat, lng: centerLng },
              latDiff, lngDiff,
              withinTolerance: latDiff <= 0.01 && lngDiff <= 0.01
            });
            
            if (latDiff <= 0.01 && lngDiff <= 0.01) {
              console.log(`✅ Found job with lenient search:`, {
                jobId: job.id,
                location: job.location_name,
                coordinates: { lat: job.latitude, lng: job.longitude }
              });
              
              jobsInRadius.push({
                id: job.id,
                name: `Company Job ${job.id.substring(0, 8)}`,
                description: `Company job from ${job.location_name || 'Unknown Location'}`,
                salary: 0,
                salary_type: 'negotiable' as const,
                location: job.location_name || 'Unknown',
                latitude: job.latitude,
                longitude: job.longitude,
                location_name: job.location_name,
                job_type: 'full-time' as const,
                experience_level: 'mid-level' as const,
                company: 'Unknown Company',
                created_at: new Date().toISOString(),
                distance: 0 // Very close, consider distance 0
              });
            }
          }
        }
      }

      console.log('🔍 Jobs search results:', {
        center: { lat: centerLat, lng: centerLng },
        radiusKm,
        totalApplications: jobs.length,
        applicationsInRadius: jobsInRadius.length,
        applications: jobs.map(j => ({
          id: j.id,
          location: j.location_name,
          lat: j.latitude,
          lng: j.longitude
        })),
        results: jobsInRadius.map(j => ({
          id: j.id,
          name: j.name,
          distance: j.distance,
          location: j.location_name
        }))
      });

      return jobsInRadius;
    } catch (error) {
      console.error('Error in searchJobsInRadius:', error);
      throw error;
    }
  }

  /**
   * Get count of jobs within radius (for quick display)
   */
  static async getJobsCountInRadius(params: RadiusSearchParams): Promise<number> {
    try {
      const jobs = await JobSearchService.searchJobsInRadius(params);
      return jobs.length;
    } catch (error) {
      console.error('Error getting jobs count:', error);
      throw error;
    }
  }

  /**
   * Calculate distance between two points using Haversine formula
   * Returns distance in kilometers
   */
  private static calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    console.log('🧮 Distance calculation input:', {
      lat1, lon1, lat2, lon2,
      lat1Type: typeof lat1,
      lon1Type: typeof lon1,
      lat2Type: typeof lat2,
      lon2Type: typeof lon2
    });

    const R = 6371; // Earth's radius in kilometers
    const dLat = JobSearchService.deg2rad(lat2 - lat1);
    const dLon = JobSearchService.deg2rad(lon2 - lon1);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(JobSearchService.deg2rad(lat1)) * Math.cos(JobSearchService.deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in kilometers
    
    console.log('🧮 Distance calculation result:', {
      dLat, dLon, a, c, distance,
      distanceKm: Math.round(distance * 100) / 100
    });
    
    return distance;
  }

  /**
   * Alternative distance calculation using simple Euclidean distance
   * This is less accurate but can help debug issues
   */
  private static calculateSimpleDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const latDiff = lat2 - lat1;
    const lonDiff = lon2 - lon1;
    const distance = Math.sqrt(latDiff * latDiff + lonDiff * lonDiff) * 111; // Rough conversion to km
    
    console.log('🧮 Simple distance calculation:', {
      latDiff, lonDiff, distance,
      distanceKm: Math.round(distance * 100) / 100
    });
    
    return distance;
  }

  /**
   * Convert degrees to radians
   */
  private static deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  /**
   * Test function to verify distance calculation
   * This can be called from console for debugging
   */
  static testDistanceCalculation() {
    // Test with the coordinates from your database
    const neussLat = 51.19817780;
    const neussLng = 6.69164760;
    
    // Test distance to a nearby point (should be close)
    const nearbyLat = 51.20000000;
    const nearbyLng = 6.70000000;
    
    const distance = JobSearchService.calculateDistance(neussLat, neussLng, nearbyLat, nearbyLng);
    
    console.log('🧪 Distance calculation test:', {
      from: { lat: neussLat, lng: neussLng, name: 'Neuss' },
      to: { lat: nearbyLat, lng: nearbyLng, name: 'Nearby point' },
      distance: Math.round(distance * 100) / 100,
      expected: 'Should be less than 1 km'
    });
    
    return distance;
  }

  /**
   * Search jobs by location name (for text-based searches)
   */
  static async searchJobsByLocation(locationQuery: string): Promise<JobSearchResult[]> {
    try {
      const { data: jobs, error } = await supabase
        .from('products')
        .select(`
          id,
          latitude,
          longitude,
          location_name
        `)
        .eq('is_job', true)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null)
        .ilike('location_name', `%${locationQuery}%`)
        .order('id', { ascending: false });

      if (error) {
        console.error('Error searching jobs by location:', error);
        throw new Error(`Failed to search jobs: ${error.message}`);
      }

      // Map the results to JobSearchResult format
      const mappedJobs: JobSearchResult[] = (jobs || []).map(job => ({
        id: job.id,
        name: `Company Job ${job.id.substring(0, 8)}`,
        description: `Company job from ${job.location_name || 'Unknown Location'}`,
        salary: 0,
        salary_type: 'negotiable' as const,
        location: job.location_name || 'Unknown',
        latitude: job.latitude,
        longitude: job.longitude,
        location_name: job.location_name,
        job_type: 'full-time' as const,
        experience_level: 'mid-level' as const,
        company: 'Unknown Company',
        created_at: new Date().toISOString()
      }));

      return mappedJobs;
    } catch (error) {
      console.error('Error in searchJobsByLocation:', error);
      throw error;
    }
  }

  /**
   * Test function to verify database connection and data
   * Call this from browser console: JobSearchService.testDatabaseConnection()
   */
  static async testDatabaseConnection() {
    try {
      console.log('🧪 Testing database connection...');
      
      // Test 1: Basic products table access
      const { data: allData, error: allError } = await supabase
        .from('products')
        .select('id, name, is_job, latitude, longitude, location_name')
        .eq('is_job', true)
        .limit(3);
        
      console.log('🧪 Test 1 - All job products (limit 3):', {
        data: allData,
        error: allError,
        count: allData?.length || 0
      });
      
      // Test 2: Job products with coordinates
      const { data: coordData, error: coordError } = await supabase
        .from('products')
        .select('id, name, latitude, longitude, location_name, is_job')
        .eq('is_job', true)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);
        
      console.log('🧪 Test 2 - Job products with coordinates:', {
        data: coordData,
        error: coordError,
        count: coordData?.length || 0
      });
      
      // Log detailed job information
      if (coordData && coordData.length > 0) {
        console.log('📋 Detailed job information:');
        coordData.forEach((job, index) => {
          console.log(`Job ${index + 1}:`, {
            id: job.id,
            name: job.name,
            location_name: job.location_name,
            latitude: job.latitude,
            longitude: job.longitude,
            is_job: job.is_job
          });
        });
      }
      
      return {
        allJobProducts: allData?.length || 0,
        withCoordinates: coordData?.length || 0,
        detailedJobs: coordData
      };
      
    } catch (error) {
      console.error('❌ Database connection test failed:', error);
      throw error;
    }
  }

  /**
   * Debug function to test radius search with specific coordinates
   * Call this from browser console: JobSearchService.debugRadiusSearch(51.19817780, 6.69164760, 50)
   */
  static async debugRadiusSearch(centerLat: number, centerLng: number, radiusKm: number) {
    try {
      console.log('🔍 Debug radius search with params:', {
        centerLat,
        centerLng,
        radiusKm
      });

      // Get all jobs with coordinates
      const { data: jobs, error } = await supabase
        .from('products')
        .select('id, latitude, longitude, location_name, name, is_job')
        .eq('is_job', true)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);

      if (error) {
        console.error('❌ Error fetching jobs:', error);
        return;
      }

      console.log('📋 All jobs in database:', jobs);

      if (!jobs || jobs.length === 0) {
        console.log('❌ No jobs found in database');
        return;
      }

      // Test distance calculation for each job
      console.log('🧮 Distance calculations:');
      jobs.forEach((job, index) => {
        const distance = JobSearchService.calculateDistance(
          centerLat,
          centerLng,
          job.latitude,
          job.longitude
        );
        
        console.log(`Job ${index + 1}:`, {
          id: job.id,
          name: job.name,
          location: job.location_name,
          coordinates: { lat: job.latitude, lng: job.longitude },
          distance: Math.round(distance * 100) / 100,
          radiusKm,
          withinRadius: distance <= radiusKm
        });
      });

      // Test the actual radius search
      const results = await JobSearchService.searchJobsInRadius({
        centerLat,
        centerLng,
        radiusKm
      });

      console.log('🎯 Radius search results:', {
        center: { lat: centerLat, lng: centerLng },
        radiusKm,
        jobsFound: results.length,
        results: results.map(r => ({
          id: r.id,
          name: r.name,
          location: r.location,
          distance: r.distance
        }))
      });

      return results;
    } catch (error) {
      console.error('❌ Debug radius search failed:', error);
      throw error;
    }
  }

  /**
   * Test function to verify distance calculation with known Neuss coordinates
   * Call this from browser console: JobSearchService.testNeussDistance()
   */
  static testNeussDistance() {
    console.log('🧪 Testing Neuss distance calculation...');
    
    // Known coordinates from your database
    const neussLat = 51.19817780;
    const neussLng = 6.69164760;
    
    // Test with the same coordinates (should be 0 distance)
    const samePointDistance = JobSearchService.calculateDistance(neussLat, neussLng, neussLat, neussLng);
    console.log('🧪 Same point distance (should be 0):', samePointDistance);
    
    // Test with slightly different coordinates
    const nearbyLat = 51.1982;
    const nearbyLng = 6.6916;
    const nearbyDistance = JobSearchService.calculateDistance(neussLat, neussLng, nearbyLat, nearbyLng);
    console.log('🧪 Nearby point distance:', nearbyDistance);
    
    // Test with coordinates from the map (51.198178, 6.691648)
    const mapLat = 51.198178;
    const mapLng = 6.691648;
    const mapDistance = JobSearchService.calculateDistance(neussLat, neussLng, mapLat, mapLng);
    console.log('🧪 Map coordinates distance:', mapDistance);
    
    return {
      samePoint: samePointDistance,
      nearby: nearbyDistance,
      mapCoordinates: mapDistance
    };
  }
}
